/** @jsx h */
import { h } from "hyperapp";
import nestable from "hyperapp-nestable";
import classNames from "classnames";
import bus from "../../../bus";
import { formatHMS } from "../../../utilities";
import { mergeStateAction, WrappedSvg } from "../../../utilities/hyperapp";
import { playerEvents } from "../../events";

import skipBackwardsIcon from "!svg-inline-loader?removeSVGTagAttrs=false!../../../player-controls/skip-backwards-icon.svg";
import skipForwardsIcon from "!svg-inline-loader?removeSVGTagAttrs=false!../../../player-controls/skip-forwards-icon.svg";

const initialState = {
  tick: Date.now(),
  tickTimer: null,
  isMouseDown: false,
  mouseDownStart: null,
  useSeekPercentage: false,
  seekToPercentage: 0,
  seekToSeconds: null,
  seekRef: null
};

const actions = {
  init: ({ duration, seekTo }) => {
    return {
      duration,
      seekTo
    };
  },
  setMouseDown: ({ isMouseDown, mouseDownStart }) => ({
    isMouseDown,
    mouseDownStart
  }),
  getMouseDown: () => ({ isMouseDown }) => isMouseDown,
  setUseSeekPercentage: useSeekPercentage => ({ useSeekPercentage }),
  onHandleCreate: el => ({ handleRef: el }),
  onCreate: el => (state, actions) => {
    el.addEventListener("mousedown", actions.onMouseDown);
    el.addEventListener("touchstart", actions.onMouseDown);
    window.addEventListener("mouseup", actions.onMouseUp);
    window.addEventListener("touchend", actions.onMouseUp);
    window.addEventListener("mousemove", actions.onMouseMove);
    el.addEventListener("touchmove", actions.onMouseMove);
    const tickTimer = window.setInterval(actions.tick, 1000);
    return {
      seekRef: el,
      tickTimer
    };
  },
  onDestroy: el => state => {
    el.removeEventListener("mousedown", actions.onMouseDown);
    el.removeEventListener("touchstart", actions.onMouseDown);
    window.removeEventListener("mouseup", actions.onMouseUp);
    window.removeEventListener("touchend", actions.onMouseUp);
    window.removeEventListener("mousemove", actions.onMouseMove);
    el.removeEventListener("touchmove", actions.onMouseMove);
    if (state.tickTimer) {
      window.clearInterval(state.tickTimer);
    }
  },
  onUpdate: ({ duration, seekTo }) => mergeStateAction({ duration, seekTo }),
  onMouseMove: (e, force, mouseDownStart) => (state, actions) => {
    if (!state.isMouseDown && !force) return;
    mouseDownStart = mouseDownStart || state.mouseDownStart;
    const cx = e.clientX || e.touches[0].clientX || 0;
    const cy = e.clientY || e.touches[0].clientY || 0;
    // Get positions
    const seekRect = state.seekRef.getBoundingClientRect();
    const handleRect = state.handleRef.getBoundingClientRect();
    // Reset mouseDownStart if cy within n of the seekRect top
    if (Math.abs(seekRect.top - cy) < 20) {
      actions.setMouseDown({ isMouseDown: true, mouseDownStart: cx });
    }

    let deltaX = cx - mouseDownStart;
    const deltaY = Math.abs(cy - handleRect.top);
    // Shrink the distance based on y position
    const decay = Math.min((deltaY * deltaY) / 2000 + 1, 100);
    deltaX = deltaX / decay;
    if (deltaX === 0) {
      return;
    }
    const absoluteX = mouseDownStart + deltaX - seekRect.left;
    // Restict between 0 and duration
    const seekToSeconds = Math.min(
      Math.max(parseInt((state.duration * absoluteX) / seekRect.width, 10), 0),
      state.duration
    );
    // Restrict between 0-100
    const seekToPercentage = Math.min(
      Math.max((absoluteX / seekRect.width) * 100, 0),
      100
    );
    return {
      useSeekPercentage: true,
      seekToSeconds,
      seekToPercentage
    };
  },
  onMouseDown: e => (state, actions) => {
    document.body.style.userSelect = "none";
    const cx = e.clientX || e.touches[0].clientX || 0;
    actions.setMouseDown({ isMouseDown: true, mouseDownStart: cx });
  },
  onMouseUp: e => (state, actions) => {
    const { seekTo, seekToSeconds } = state;
    document.body.style.userSelect = "auto";
    if (state.mouseUpTimer) {
      clearTimeout(state.mouseUpTimer);
    }
    if (seekToSeconds != null) {
      seekTo(seekToSeconds);
    }
    actions.setMouseDown({ isMouseDown: false, mouseDownStart: null });
    return {
      seekToSeconds: null,
      mouseUpTimer: window.setTimeout(() => {
        if (!actions.getMouseDown()) {
          actions.setUseSeekPercentage(false);
        }
      }, 2000)
    };
  },
  tick: () => ({
    tick: Date.now()
  })
};

function preventDefault(e) {
  e.preventDefault();
}

const PlayerTime = nestable(
  initialState,
  actions,
  (state, actions) => props => {
    const { duration, getCurrentTime } = props;
    const currentTime = getCurrentTime();
    const { isMouseDown, useSeekPercentage, seekTo, seekToPercentage } = state;

    // Calculated values
    const remaining = (duration - currentTime) * -1;
    const showTo = duration >= 3600 ? "h" : "m";
    let elapsedPercentage = Math.min((currentTime / duration) * 100, 100);
    if (window.isNaN(elapsedPercentage)) {
      elapsedPercentage = 0;
    }

    const formattedCurrentTime = formatHMS(currentTime, showTo);
    const formattedDuration = formatHMS(duration, showTo);
    const formattedRemaining = formatHMS(remaining, showTo);

    const visualPercentage = useSeekPercentage
      ? seekToPercentage
      : elapsedPercentage;
    const visualElapsed = useSeekPercentage
      ? formatHMS((duration * seekToPercentage) / 100, showTo)
      : formattedCurrentTime;

    // Emit time update
    bus.emit(playerEvents.TIME_UPDATE, {
      currentTime,
      remaining,
      duration,
      elapsedPercentage,
      formattedCurrentTime,
      formattedDuration,
      formattedRemaining
    });

    return (
      <div class="player-controls__time-wrapper d-flex flex-items-v-center mt-small mb-small">
        <button
          class="player-controls__skip-backwards player-controls__icon"
          onclick={e => {
            e.preventDefault();
            seekTo(Math.max(currentTime - 15, 0));
          }}
        >
          <span class="hide-visually">Seek backwards 15 seconds</span>
          <WrappedSvg data={skipBackwardsIcon} />
        </button>
        <div class="player-controls__time flex-1 px-xsmall">
          <div
            class="player-controls__seek flex-1"
            oncreate={actions.onCreate}
            ondestroy={actions.onDestroy}
            onupdate={() => actions.onUpdate(props)}
            draggable={false}
          >
            <div class="player-controls__seek-fill" />
            <div
              class="player-controls__seek-elapsed"
              style={{ width: `${visualPercentage}%` }}
            />
            <button
              oncreate={actions.onHandleCreate}
              class="player-controls__seek-handle"
              style={{
                left: `${visualPercentage}%`
              }}
              onclick={preventDefault}
            >
              <span
                class={classNames(
                  "player-controls__seek-tooltip",
                  "t-alternate",
                  "c-grey-light",
                  "t-xsmall",
                  {
                    "player-controls__seek-tooltip--visible": isMouseDown
                  }
                )}
              >
                {visualElapsed}
              </span>
              <span class="hide-visually">Seek to</span>
            </button>
          </div>
          <div class="player-controls__timing d-flex flex-items-v-center">
            <div class="player-controls__elapsed t-alternate c-grey-light t-xsmall flex-left">
              <span class="hide-visually">Elapsed:</span>
              {formattedCurrentTime}
            </div>
            <div class="player-controls__remaining t-alternate c-grey-light t-xsmall flex-right">
              <span class="hide-visually">Remaining:</span>
              {formattedRemaining}
            </div>
          </div>
        </div>
        <button
          class="player-controls__skip-forwards player-controls__icon"
          onclick={e => {
            e.preventDefault();
            seekTo(Math.min(currentTime + 15, duration - 1));
          }}
        >
          <span class="hide-visually">Seek forwards 15 seconds</span>
          <WrappedSvg data={skipForwardsIcon} />
        </button>
      </div>
    );
  }
);

export default PlayerTime;
